import authSession from "../../lib/authSession";
import { API_URL } from "../../constants";
import { safeFetch, explainApiError } from "../../utils/toDisplayString";

/**
 * resourcePersonSubmissionStore — grading slice (Stage 7).
 *
 *   GET   /api/resource-person/courses/:id/submissions
 *   PATCH /api/resource-person/courses/:id/submissions/:submissionId/grade
 *         { score, feedback? } — a pass completes the lesson and unlocks next.
 */

export const resourcePersonSubmissionInitialState = {
  submissions: [],
  submissionsLoading: false,
  submissionsError: null,
  submissionsTotal: 0,
  submissionsPages: 1,
  gradeMutationLoading: false,
  gradeMutationError: null,
};

function buildSubUrl(path, params) {
  const query = new URLSearchParams(
    Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== "")
  ).toString();
  return `${API_URL.replace(/\/$/, "")}${path}${query ? `?${query}` : ""}`;
}

function subRequestOptions(method = "GET", body) {
  const token = authSession.getToken();
  const headers = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (token) headers.Authorization = `Bearer ${token}`;
  return {
    method,
    headers,
    credentials: "omit",
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  };
}

async function requestSubApi(path, options = {}) {
  if (!API_URL) throw new Error("REACT_APP_API_BASE_URL is not configured.");
  const response = await safeFetch(buildSubUrl(path, options.params), subRequestOptions(options.method, options.body));
  const text = await response.text();
  let data = {};
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      if (text.trim().startsWith("<")) throw new Error(`Submissions API returned HTML (${response.status}).`);
      throw new Error(`Submissions API returned a non-JSON response (${response.status}).`);
    }
  }
  if (!response.ok) {
    const err = new Error(explainApiError(response.status, data || {}, `Request failed with status ${response.status}.`));
    err.status = response.status;
    err.payload = data;
    throw err;
  }
  return data;
}

function subListOf(data) {
  const payload =
    data?.data && typeof data.data === "object" && !Array.isArray(data.data) ? data.data : data || {};
  for (const key of ["submissions", "items", "data"]) {
    if (Array.isArray(payload[key])) return payload[key];
  }
  return Array.isArray(payload) ? payload : [];
}

export function createResourcePersonSubmissionActions(set, get) {
  return {
    // GET /api/resource-person/courses/:id/submissions
    fetchSubmissions: async (courseId, params = {}) => {
      set({ submissionsLoading: true, submissionsError: null });
      try {
        const data = await requestSubApi(`/api/resource-person/courses/${courseId}/submissions`, {
          params,
        });
        const payload =
          data?.data && typeof data.data === "object" && !Array.isArray(data.data) ? data.data : data || {};
        const items = subListOf(data);
        set({
          submissionsLoading: false,
          submissions: items,
          submissionsTotal: payload.total ?? payload.totalItems ?? items.length,
          submissionsPages: payload.totalPages ?? payload.pages ?? 1,
        });
        return { success: true, submissions: items };
      } catch (error) {
        set({ submissionsLoading: false, submissionsError: error.message, submissions: [] });
        return { success: false, error: error.message, status: error.status };
      }
    },

    /**
     * Pending-submission count for one course. Read-only: it deliberately does
     * not write the shared `submissions` slice so a sidebar badge can poll it
     * without clobbering the grading page.
     */
    fetchPendingSubmissionCount: async (courseId) => {
      try {
        const data = await requestSubApi(
          `/api/resource-person/courses/${courseId}/submissions`,
          { params: { status: "PENDING", page: 1, limit: 1 } }
        );
        const payload =
          data?.data && typeof data.data === "object" && !Array.isArray(data.data)
            ? data.data
            : data || {};
        return {
          success: true,
          count: Number(payload.total ?? payload.totalItems ?? 0) || 0,
        };
      } catch (error) {
        return { success: false, error: error.message, count: 0 };
      }
    },

    // PATCH .../submissions/:submissionId/grade { score, feedback? }
    gradeSubmission: async (courseId, submissionId, { score, feedback } = {}) => {
      set({ gradeMutationLoading: true, gradeMutationError: null });
      try {
        const data = await requestSubApi(
          `/api/resource-person/courses/${courseId}/submissions/${submissionId}/grade`,
          { method: "PATCH", body: feedback === undefined ? { score } : { score, feedback } }
        );
        const updated =
          data?.data && typeof data.data === "object" && !Array.isArray(data.data)
            ? data.data
            : data?.submission ?? data ?? {};
        set((state) => ({
          gradeMutationLoading: false,
          submissions: (state.submissions || []).map((item) =>
            item?.id === submissionId ? { ...item, ...(updated || {}), id: submissionId } : item
          ),
        }));
        return { success: true, submission: updated };
      } catch (error) {
        set({ gradeMutationLoading: false, gradeMutationError: error.message });
        return { success: false, error: error.message, status: error.status };
      }
    },
  };
}

const resourcePersonSubmissionActions = createResourcePersonSubmissionActions;
export default resourcePersonSubmissionActions;
